import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, of, timer } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { CoreModule } from '../core.module';
import { ServerConnectionService } from './server-connection.service';
import { environment } from '../../../environments/environment';

interface VersionResponse {
	version: string;
}

interface VersionCheckWindow extends Window {
	appVersion?: string;
}
declare const window: VersionCheckWindow;

const FOCUS_CHECK_DEBOUNCE_MS = 30 * 1000;

@Injectable({
	providedIn: CoreModule,
})
export class VersionCheckService {
	public readonly currentVersion: string;

	public readonly newVersionAvailable$ = new BehaviorSubject<boolean>(false);

	private stopPolling$ = new Subject<void>();

	private lastCheckAt = 0;

	constructor(private scs: ServerConnectionService) {
		/**
		 * Initialize in constructor because the inline property might get
		 * evaluated before the injected script that has the version is parsed.
		 */
		this.currentVersion = window.appVersion || 'unknown';
		/* this.currentVersion = 'unknown'; */
		if (!environment.versionCheck.enabled) {
			return;
		}

		timer(0, environment.versionCheck.intervalMs).pipe(
			takeUntil(this.stopPolling$),
		).subscribe(() => this.check());

		document.addEventListener('visibilitychange', () => this.onVisibilityChange());
	}

	private onVisibilityChange(): void {
		if (document.visibilityState !== 'visible') {
			return;
		}

		if (Date.now() - this.lastCheckAt < FOCUS_CHECK_DEBOUNCE_MS) {
			return;
		}

		this.check();
	}

	private check(): void {
		this.lastCheckAt = Date.now();

		this.scs.get$<VersionResponse>('/server/version', { t: Date.now() }).pipe(
			catchError(() => of(null)),
		).subscribe((response) => {
			if (!response?.version) {
				return;
			}

			if (response.version !== this.currentVersion) {
				this.newVersionAvailable$.next(true);
				this.stopPolling$.next();
			}
		});
	}
}
